import { useNethoneCheckMutation } from '@src/hooks/queries/transactions';
import { isUndefined } from '@src/utils/type';
import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useEnv } from './env-context';
import loadjs from 'loadjs';

interface NethoneContext {
  handleSetNethoneTxref: (txref: string) => void;
  isNethoneCheck: boolean;
  isNethoneFailed: boolean;
}

interface ExtendWindow extends Window {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dftp?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  profileCompleted?: any;
}

interface ProfilerOptions {
  attemptReference: string;
  sensitiveFields: string[];
}

type NethoneVariant = 'authenticated' | 'not-authenticated' | null;

const Context = createContext<NethoneContext | undefined>(undefined);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const NethoneProvider = (props: React.PropsWithChildren<any>) => {
  const [isNethoneCheck, setIsNethoneCheck] = useState(false);
  const [isNethoneFailed, setIsNethoneFailed] = useState(false);
  const [usedTxref, setUsedTxref] = useState('');
  const [variant, setVariant] = useState<NethoneVariant>(null);

  const { NETHONE_SRC } = useEnv();
  const nethoneMutation = useNethoneCheckMutation();

  const handleSetNethoneTxref = async (txref: string, variant?: NethoneVariant) => {
    setIsNethoneCheck(false);
    setIsNethoneFailed(false);

    if (variant === 'not-authenticated') {
      setIsNethoneCheck(true);
      return;
    }

    setVariant(variant || 'authenticated');

    if (txref) {
      if ((window as ExtendWindow)?.dftp) {
        (window as ExtendWindow).dftp = null;
      }

      await handleLoad();

      // from the docs, this should be set once till reload
      // https://panel.nethone.io/api-docs/fingerprint/web_services_cdn.html#page-lifecycle
      handleInit({
        attemptReference: `${txref}`, // inserted by the backend
        sensitiveFields: ['ccn', 'cvv'] // list of sensitive fields
      });
      setUsedTxref(txref);
    }
  };

  const handleLoad = useCallback(async () => {
    if ((window as ExtendWindow)?.dftp) {
      return;
    } else {
      try {
        await loadjs(NETHONE_SRC, {
          returnPromise: true,
          before: (path, scriptEl: HTMLScriptElement) => (scriptEl.crossOrigin = 'use-credentials')
        });
      } catch (error) {
        console.error(error, 'err');
      }
    }
  }, [NETHONE_SRC]);

  const handleInit = useCallback((profilerOptions: ProfilerOptions) => {
    if ((window as ExtendWindow)?.dftp) {
      (window as ExtendWindow).dftp?.init(profilerOptions);
    }
  }, []);

  const handleNethoneCheck = useCallback(async () => {
    try {
      // if ((window as ExtendWindow)?.dftp) {
      //   console.log('nethone profile completed init');
      //   await (window as ExtendWindow).dftp?.profileCompleted();
      //   console.log('nethone profile completed done');
      // }

      const nethoneData = await nethoneMutation.mutateAsync({ txref: usedTxref });

      if (nethoneData?.ResponseMessage === 'Successful') {
        setIsNethoneCheck(true);
        setIsNethoneFailed(false);

        if ((window as ExtendWindow)?.dftp) {
          (window as ExtendWindow).dftp?.finishBehavGathering();
        }
      }
    } catch (error) {
      setIsNethoneFailed(true);
      setIsNethoneCheck(false);

      if ((window as ExtendWindow)?.dftp) {
        (window as ExtendWindow).dftp?.finishBehavGathering();
      }
    }
  }, [usedTxref]);

  // effect to delay nethone check by 3s
  useEffect(() => {
    let timer: NodeJS.Timer;
    const NETHONE_DELAY_TIME = 3000;

    if (usedTxref && variant === 'authenticated') {
      timer = setTimeout(() => {
        handleNethoneCheck();
      }, NETHONE_DELAY_TIME);
    }

    return () => clearTimeout(timer);
  }, [usedTxref, handleNethoneCheck, variant]);

  const value: NethoneContext = { handleSetNethoneTxref, isNethoneCheck, isNethoneFailed };

  return <Context.Provider value={value} {...props} />;
};

const useNethone = () => {
  const context = React.useContext(Context);
  if (isUndefined(context)) {
    throw new Error(`useNethone must be used within a NethoneProvider`);
  }

  return { ...context };
};

export type { NethoneContext };
export { NethoneProvider, useNethone };
